import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getProviderEmpreitas } from '../utils/api';
import { FolderKanban, Hammer, CheckCircle2, Clock, Receipt } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ProviderDashboard = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [legacy, setLegacy] = useState(null);
  const [empreitas, setEmpreitas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const token = localStorage.getItem('token');
        const [mRes, lRes, eRes] = await Promise.all([
          axios.get(`${API_URL}/dashboard/metrics`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/provider/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
          getProviderEmpreitas().catch(() => ({ data: [] }))
        ]);
        setMetrics(mRes.data);
        setLegacy(lRes.data);
        setEmpreitas(eRes.data || []);
      } catch (error) {
        console.error('Error fetching provider dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return <div className="text-slate-500">Carregando…</div>;
  }

  return (
    <div className="space-y-6" data-testid="provider-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900">
          Olá, {user?.name?.split(' ')[0] || 'Prestador'}
        </h1>
        <p className="text-slate-600 font-primary mt-1">Acompanhe suas empreitas e pagamentos</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="provider-summary-cards">
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="card-active-projects">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-secondary">
            <FolderKanban className="h-4 w-4 text-blue-600" /> Projetos Ativos
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">{metrics?.active_projects_count ?? 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="card-active-empreitas">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-secondary">
            <Hammer className="h-4 w-4 text-amber-600" /> Empreitas Ativas
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">{metrics?.active_empreitas_count ?? 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="card-total-received">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-secondary">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Total Recebido
          </div>
          <p className="text-xl font-bold text-emerald-700 mt-2 tabular-nums">{fmtMoney(metrics?.total_received)}</p>
          <p className="text-xs text-slate-500 mt-1">Pagamentos confirmados</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="card-to-receive">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-secondary">
            <Clock className="h-4 w-4 text-amber-600" /> A Receber
          </div>
          <p className="text-xl font-bold text-amber-700 mt-2 tabular-nums">{fmtMoney(metrics?.a_receber_total)}</p>
          <div className="mt-2 space-y-0.5 text-xs text-slate-500">
            <p>Empreita: <span className="tabular-nums">{fmtMoney(metrics?.a_receber_empreita)}</span></p>
            <p>Material ({metrics?.a_receber_material_count ?? 0}): <span className="tabular-nums">{fmtMoney(metrics?.a_receber_material)}</span></p>
          </div>
        </div>
      </div>

      {/* Active Empreitas */}
      {empreitas.length > 0 && (
        <div>
          <h2 className="text-lg font-secondary font-bold uppercase tracking-tight text-slate-900 mb-4 flex items-center gap-2">
            <Hammer className="h-5 w-5 text-amber-600" /> Empreitas Ativas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="provider-empreitas-list">
            {empreitas.map((emp) => (
              <div key={emp.empreita_id} className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="provider-empreita-card">
                <p className="font-semibold text-slate-900 break-words">{emp.empreita_nome}</p>
                <p className="text-xs text-slate-500 mt-0.5 break-words">
                  Obra: {emp.project_name} · Serviço: {emp.codigo_servico}
                </p>
                <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Acordado</p>
                    <p className="font-semibold text-slate-900 tabular-nums">{fmtMoney(emp.valor_custo_acordado)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Recebido</p>
                    <p className="font-semibold text-emerald-600 tabular-nums">{fmtMoney(emp.total_pago)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Saldo</p>
                    <p className={`font-semibold tabular-nums ${emp.saldo >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                      {fmtMoney(emp.saldo)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Launches (legacy section) */}
      {legacy?.recent_launches?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <h2 className="text-lg font-secondary font-bold uppercase tracking-tight text-slate-900 mb-4 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-purple-600" /> Lançamentos Recentes
          </h2>
          <div className="space-y-2">
            {legacy.recent_launches.slice(0, 8).map((l) => (
              <div key={l.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-sm">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {l.empreita_nome || l.service_description || l.project_name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {fmtMoney(l.amount)} · {new Date(l.launch_date).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-sm font-medium shrink-0 ${
                  l.status === 'paid' ? 'text-emerald-600 bg-emerald-50' :
                  l.status === 'submitted_receipt' ? 'text-blue-600 bg-blue-50' :
                  'text-amber-600 bg-amber-50'
                }`}>
                  {l.status === 'paid' ? 'Pago' : l.status === 'submitted_receipt' ? 'Comprovante' : 'Pendente'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderDashboard;
